import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Sparkles, Loader2 } from "lucide-react";

const PLANS = [
  {
    id: "monthly",
    name: "Monthly",
    duration: "per month",
    inr: { current: 5000, original: null, badge: null },
    usd: { current: 50, original: null, badge: null },
    popular: false,
  },
  {
    id: "quarterly",
    name: "Quarterly",
    duration: "3 months",
    inr: { current: 11000, original: 15000, badge: "Save 27% · ₹4,000 OFF" },
    usd: { current: 110, original: 150, badge: "Save 27% · $40 OFF" },
    popular: true,
  },
  {
    id: "yearly",
    name: "Yearly",
    duration: "12 months",
    inr: { current: 50000, original: 60000, badge: "Save 17% · ₹10,000 OFF" },
    usd: { current: 500, original: 600, badge: "Save 17% · $100 OFF" },
    popular: false,
  },
];

function format(amount, currency) {
  return currency === "INR"
    ? `₹${amount.toLocaleString("en-IN")}`
    : `$${amount.toLocaleString("en-US")}`;
}

export default function Pricing({ country, onChoose, busyPlan, forceCurrency }) {
  const isIndiaByTimezone =
    typeof Intl !== "undefined" &&
    Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Kolkata";
  const defaultCurrency =
    forceCurrency || ((country ? country.toLowerCase() === "india" : isIndiaByTimezone) ? "INR" : "USD");
  const [currency, setCurrency] = useState(defaultCurrency);
  const isINR = currency === "INR";
  const lockCurrency = !!forceCurrency;

  const comingSoon = () =>
    toast.info("Coming Soon — secure online payments via Razorpay are launching shortly.", {
      description: "For early access or offline activation, write to support@mdviacare.com.",
    });

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-64 rounded-full bg-blue-200/40 blur-3xl pointer-events-none" />
      <div className="relative max-w-[1100px] mx-auto">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#1E5AA8]">Simple, transparent pricing</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Choose the plan that fits your clinic</h2>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto text-sm sm:text-base">
            One gentle subscription. Every plan includes the full MDvia Care clinical suite — no hidden fees.
          </p>

          {/* Currency toggle */}
          {!lockCurrency && (
            <div className="mt-6 inline-flex items-center rounded-full bg-white border border-slate-200 p-1 shadow-sm" role="group" aria-label="Currency">
              <button data-testid="currency-inr" onClick={() => setCurrency("INR")}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${isINR ? "bg-[#1E5AA8] text-white" : "text-slate-500 hover:text-slate-800"}`}>
                INR · ₹
              </button>
              <button data-testid="currency-usd" onClick={() => setCurrency("USD")}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${!isINR ? "bg-[#1E5AA8] text-white" : "text-slate-500 hover:text-slate-800"}`}>
                USD · $
              </button>
            </div>
          )}
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan, i) => {
            const price = isINR ? plan.inr : plan.usd;
            return (
              <motion.div
                key={plan.id}
                data-testid={`plan-${plan.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`relative flex flex-col rounded-3xl bg-white p-7 shadow-sm border ${
                  plan.popular
                    ? "border-[#1E5AA8] ring-2 ring-[#1E5AA8]/20 shadow-xl shadow-blue-900/10 md:-translate-y-2"
                    : "border-slate-200"
                }`}
              >
                {plan.popular && (
                  <span data-testid="plan-popular-badge" className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-[#1E5AA8] text-white text-[11px] font-bold px-3 py-1 shadow">
                    <Sparkles className="w-3 h-3" /> Most Popular
                  </span>
                )}

                <h3 className="font-display text-xl font-bold text-slate-900">{plan.name}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{plan.duration}</p>

                <div className="mt-5 flex items-end gap-2">
                  <span data-testid={`plan-${plan.id}-price`} className="font-display text-4xl font-bold text-[#123C73]">
                    {format(price.current, currency)}
                  </span>
                  {price.original && (
                    <span data-testid={`plan-${plan.id}-original`} className="text-lg text-slate-400 line-through mb-1">
                      {format(price.original, currency)}
                    </span>
                  )}
                </div>

                {price.badge && (
                  <span data-testid={`plan-${plan.id}-badge`}
                    className="mt-3 inline-flex w-fit items-center rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold px-3 py-1">
                    {price.badge}
                  </span>
                )}

                <ul className="mt-6 space-y-2.5 text-sm text-slate-600 flex-1">
                  {["Unlimited consultations & AI prescriptions", "Patient registry, appointments & follow-ups", "Printable OPD sheets & certificates", "One-tap WhatsApp / SMS / Email sharing", "Works in 130+ languages & global guidelines"].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`btn-go-premium-${plan.id}`}
                  onClick={onChoose ? () => onChoose(plan.id) : comingSoon}
                  disabled={busyPlan === plan.id}
                  className={`mt-7 w-full py-3 rounded-xl font-semibold transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 ${
                    plan.popular
                      ? "bg-[#1E5AA8] hover:bg-[#123C73] text-white shadow-lg shadow-blue-900/20"
                      : "border-2 border-[#1E5AA8] text-[#1E5AA8] hover:bg-[#EBF3FB]"
                  }`}
                >
                  {busyPlan === plan.id && <Loader2 className="w-4 h-4 animate-spin" />}
                  Go Premium
                </button>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-slate-400">
          {isINR ? "Prices in INR for clinics in India." : "Prices in USD for clinics outside India."} Cancel anytime. Secure payments powered by Razorpay.
        </p>
      </div>
    </section>
  );
}
